<template>
  <div>
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" @click="openEdit()">新增部门</el-button>
    </div>
    <el-table
      :data="deptTree"
      border
      row-key="id"
      default-expand-all
      :tree-props="{ children: 'children' }"
      v-loading="loading"
    >
      <el-table-column prop="deptName" label="部门名称" min-width="220" />
      <el-table-column prop="deptCode" label="部门编码" min-width="140" />
      <el-table-column prop="sortNo" label="排序" width="90" />
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openEdit(null, row.id)">新增下级</el-button>
          <el-button type="text" @click="openEdit(row)">编辑</el-button>
          <el-button type="text" style="color:#ef4444" @click="onDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <p v-if="!loading && !list.length" class="empty">暂无部门，请先新增</p>

    <el-dialog :title="form.id ? '编辑部门' : '新增部门'" :visible.sync="visible" width="480px">
      <el-form :model="form" label-width="90px">
        <el-form-item label="部门编码" required>
          <el-input v-model="form.deptCode" />
        </el-form-item>
        <el-form-item label="部门名称" required>
          <el-input v-model="form.deptName" />
        </el-form-item>
        <el-form-item label="上级部门">
          <el-select v-model="form.parentId" placeholder="顶级部门" style="width:100%">
            <el-option :value="0" label="顶级部门" />
            <el-option
              v-for="d in parentOptions"
              :key="d.id"
              :label="d.label"
              :value="d.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sortNo" :min="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listDepts, saveDept, deleteDept } from '../../api/sys'

function buildTree(list) {
  const nodes = (list || []).map((d) => ({ ...d, children: [] }))
  const map = {}
  nodes.forEach((n) => {
    map[n.id] = n
  })
  const roots = []
  nodes.forEach((n) => {
    const pid = n.parentId == null ? 0 : Number(n.parentId)
    if (pid && map[pid]) {
      map[pid].children.push(n)
    } else {
      roots.push(n)
    }
  })
  const sortNodes = (arr) => {
    arr.sort((a, b) => (a.sortNo || 0) - (b.sortNo || 0) || a.id - b.id)
    arr.forEach((n) => {
      if (n.children && n.children.length) {
        sortNodes(n.children)
      } else {
        delete n.children
      }
    })
  }
  sortNodes(roots)
  return roots
}

function flattenWithLabel(nodes, depth, excludeId, out) {
  ;(nodes || []).forEach((n) => {
    if (excludeId && n.id === excludeId) return
    out.push({
      id: n.id,
      label: (depth ? '　'.repeat(depth) : '') + (n.deptName || n.deptCode)
    })
    flattenWithLabel(n.children, depth + 1, excludeId, out)
  })
}

function collectDescendantIds(node, ids) {
  ;(node.children || []).forEach((c) => {
    ids.add(c.id)
    collectDescendantIds(c, ids)
  })
}

export default {
  name: 'SysDept',
  data() {
    return {
      list: [],
      loading: false,
      saving: false,
      visible: false,
      form: {}
    }
  },
  computed: {
    deptTree() {
      return buildTree(this.list)
    },
    parentOptions() {
      const exclude = new Set()
      if (this.form && this.form.id) {
        exclude.add(this.form.id)
        const self = this.findNode(this.deptTree, this.form.id)
        if (self) collectDescendantIds(self, exclude)
      }
      const out = []
      flattenWithLabel(this.deptTree, 0, null, out)
      return out.filter((d) => !exclude.has(d.id))
    }
  },
  created() {
    this.load()
  },
  methods: {
    findNode(nodes, id) {
      for (const n of nodes || []) {
        if (n.id === id) return n
        const hit = this.findNode(n.children, id)
        if (hit) return hit
      }
      return null
    },
    async load() {
      this.loading = true
      try {
        const res = await listDepts()
        this.list = res.data || []
      } finally {
        this.loading = false
      }
    },
    openEdit(row, parentId) {
      this.form = row
        ? {
            id: row.id,
            deptCode: row.deptCode,
            deptName: row.deptName || '',
            parentId: row.parentId == null ? 0 : row.parentId,
            sortNo: row.sortNo == null ? 10 : row.sortNo
          }
        : {
            id: null,
            deptCode: '',
            deptName: '',
            parentId: parentId || 0,
            sortNo: (this.list.length + 1) * 10
          }
      this.visible = true
    },
    async save() {
      const f = this.form
      if (!f.deptCode) return this.$message.warning('请填写部门编码')
      if (!f.deptName) return this.$message.warning('请填写部门名称')
      this.saving = true
      try {
        await saveDept(f)
        this.$message.success('已保存')
        this.visible = false
        this.load()
      } finally {
        this.saving = false
      }
    },
    onDelete(row) {
      this.$confirm(`确认删除部门「${row.deptName || row.deptCode}」？`, '提示', { type: 'warning' })
        .then(async () => {
          await deleteDept(row.id)
          this.$message.success('已删除')
          this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.empty {
  margin-top: 16px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}
</style>
